import React, { useEffect, useState } from "react";
import locales from "../../I18n/locales";
import { parseJson } from "../../utils";
import { Dropdown, TextArea } from "colbi_web_ui/lib/components/inputs";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import defaultStyles from "./DescriptionsLocales.module.sass";
import { SovosIcon } from "mosaic-react";
import { HighlightOff } from "mosaic-react-icons";

/**
 * Description Locales Component - This component is designed to facilitate the listing and editing of key
 * stored as JSON objects, where each key represents a location.
 *
 * @component
 * @param {Object} props - The component's props.
 * @param {object} props.jsonObject - The Json containing the data ( key - value / Key is the locale )
 * @param {function} props.handleChange - Handle Change
 * @param {string} props.keyToChange - Key to Change on the handle Change
 * @param {string} props.inputTitle - title of the input
 * @param {string} props.inputPlaceholder - placeholder of the input
 * @param {string} props.containerTitle - placeholder of the input
 * @param {object} props.styles - styles for the component
 * @returns {JSX.Element} The rendered React element.
 */
const JsonLocalesEditor = ({
    jsonObject,
    handleChange,
    keyToChange,
    inputTitle,
    inputPlaceholder,
    containerTitle,
    styles,
}) => {
    const selectedStyles = styles ? styles : defaultStyles;
    const [itemsByLocale, setItemsByLocale] = useState({});
    const localesData = locales();
    const { i18n } = useGlobalValue();
    const availableLocales = parseAvailableLocales(localesData, itemsByLocale);
    let itemsElements = buildItemsElements(itemsByLocale, availableLocales, localesData);

    useEffect(() => {
        setItemsByLocale(parseJson(jsonObject) || {});
    }, [jsonObject]);

    function parseAvailableLocales(localeArray, currentUsedLocales) {
        return (localeArray || []).filter(
            ({ id }) => !Object.keys(currentUsedLocales || {}).includes(id),
        );
    }

    function buildItemsElements(items, availableLocalesArray, localesArray) {
        return (typeof items === "object" ? Object.keys(items) : []).map((locale, index) => {
            return (
                <div key={index} className={selectedStyles["item-entry"]}>
                    <div className={selectedStyles["locale-container"]}>
                        <Dropdown
                            placeholder={""}
                            className={selectedStyles["period-dropdown"]}
                            secondary
                            options={availableLocalesArray || []}
                            value={localesArray.find(({ id }) => id === locale)}
                            onChange={(e) => {
                                const updatedItems = {
                                    ...itemsByLocale,
                                    ...{ [e.target.value.id]: itemsByLocale[locale] || "" },
                                };
                                delete updatedItems[locale];
                                handleChange(e, keyToChange, JSON.stringify(updatedItems));
                            }}
                            i18n={i18n}
                        />
                        <div className={selectedStyles["remove-button-container"]}>
                            <button
                                type="button"
                                className={selectedStyles["remove-button"]}
                                onClick={(e) => {
                                    const updatedItems = { ...itemsByLocale };
                                    delete updatedItems[locale];
                                    setItemsByLocale({ ...updatedItems });
                                    handleChange(e, keyToChange, JSON.stringify(updatedItems));
                                }}
                            >
                                <SovosIcon>
                                    <HighlightOff />
                                </SovosIcon>
                            </button>
                        </div>
                    </div>
                    <div className={selectedStyles["translation-container"]}>
                        <TextArea
                            type="text"
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck="false"
                            name={i18n`${inputTitle}`}
                            placeholder={i18n`${inputPlaceholder}`}
                            value={(itemsByLocale && itemsByLocale[locale]) || ""}
                            onChange={(e) => {
                                const itemsUpdated = {
                                    ...itemsByLocale,
                                    ...{ [locale]: e.target.value },
                                };
                                setItemsByLocale(itemsUpdated);
                                handleChange(e, keyToChange, JSON.stringify(itemsUpdated));
                            }}
                        />
                    </div>
                </div>
            );
        });
    }

    return (
        <>
            <h5 className={selectedStyles["input-label"]}>{i18n`${containerTitle}`}</h5>
            <div className={selectedStyles["flex"]}>
                <div className={selectedStyles["input-field"]}>
                    <div>
                        {(itemsElements || []).length ? (
                            itemsElements
                        ) : (
                            <small>{i18n`No descriptions assigned`}</small>
                        )}
                    </div>
                    <div>
                        <button
                            className={selectedStyles["add-button"]}
                            type="button"
                            onClick={(e) => {
                                const itemsUpdated = {
                                    ...itemsByLocale,
                                    ...{ [availableLocales[0]["id"]]: "" },
                                };

                                setItemsByLocale(itemsUpdated);
                                handleChange(e, keyToChange, JSON.stringify(itemsUpdated));
                            }}
                        >
                            {availableLocales && availableLocales.length ? (
                                <small>{i18n`[+] Add new Description`}</small>
                            ) : null}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default JsonLocalesEditor;
